import { useEffect, useRef, useState } from 'react';
import useModal from '../../../../hooks/use-close-modal.hook';
import { CPDService, type EffectiveCpdConfig } from '../../../../services/cpd.service';
import { AlertService } from '../../../../services/alert.service';
import Icon from '../../../../components/forms/icon.component';
import Input from '../../../../components/forms/input.component';

interface Props {
  show: boolean;
  onRequestClose: () => void;
  /** Valores iniciales (p. ej. desde la fila de la tabla). */
  initial?: { operatorId?: string; routeGroupId?: number | string };
}

const LAYER_LABEL: Record<string, string> = {
  default: 'Default (hardcoded)',
  template: 'Template',
  route: 'Ruta',
  operator: 'Operador',
  route_operator: 'Operador × Ruta'
};

const LAYER_ORDER = ['default', 'template', 'route', 'operator', 'route_operator'];

/**
 * Preview de la política CPD efectiva: GET /v1/cpd/resolve?operatorId=&routeGroupId=
 * Muestra la cadena de capas aplicadas (de menos a más específica) y la config final,
 * que es exactamente lo que el FS recibe por /v1/internal/cpd/resolve.ini.
 */
export default function CpdResolvePreviewModal({ show, onRequestClose, initial }: Props) {
  const modalRef = useRef<HTMLDivElement>(null);
  useModal(modalRef, show);

  const [operatorId, setOperatorId] = useState(initial?.operatorId ?? '');
  const [routeGroupId, setRouteGroupId] = useState(
    initial?.routeGroupId !== undefined && initial?.routeGroupId !== null
      ? String(initial.routeGroupId)
      : ''
  );
  const [result, setResult] = useState<EffectiveCpdConfig | null>(null);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    try {
      setLoading(true);
      const params: { operatorId?: string; routeGroupId?: number } = {};
      if (operatorId.trim()) params.operatorId = operatorId.trim();
      if (routeGroupId.trim() !== '') params.routeGroupId = Number(routeGroupId);
      const res = await CPDService.resolve(params);
      setResult(res);
    } catch (error: any) {
      setResult(null);
      AlertService.toastError(error?.error || 'No se pudo resolver la política');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (show) run();
  }, [show]);

  const close = () => {
    document.querySelectorAll('.modal-backdrop').forEach((el) => el.remove());
    document.body.classList.remove('modal-open');
    document.body.style.overflow = '';
    document.body.style.paddingRight = '';
    onRequestClose();
  };

  const applied = new Set(result?.resolution.applied ?? []);
  const cfg = result?.config ?? {};
  const th = cfg.thresholds ?? {};

  return (
    <div
      className="modal fade show d-block"
      ref={modalRef}
      style={{ backgroundColor: 'rgba(0,0,0,0.45)' }}
      data-bs-backdrop="static"
    >
      <div className="modal-dialog modal-dialog-centered mw-900px">
        <div className="modal-content shadow-lg border-0">
          <div className="modal-header justify-content-end border-0 pb-0 pt-4 px-6">
            <button
              type="button"
              className="btn btn-sm btn-icon btn-light-danger rounded-circle"
              onClick={close}
            >
              <i className="ki-duotone ki-cross fs-2">
                <span className="path1" />
                <span className="path2" />
              </i>
            </button>
          </div>

          <div className="modal-body pt-0 pb-10 px-6 px-xl-12">
            <div className="text-center mb-6">
              <h1 className="mb-2 fs-2x">Política CPD efectiva</h1>
              <p className="text-muted fw-semibold fs-6">
                Lo que el FS recibe para esta combinación operador / grupo de ruta.
              </p>
              <div className="separator border-2 my-4"></div>
            </div>

            <form
              className="row g-4 align-items-end mb-6"
              noValidate
              onSubmit={(e) => {
                e.preventDefault();
                run();
              }}
            >
              <div className="col-md-5">
                <Input
                  label="operatorId"
                  optional
                  placeholder="vacío = sin capa operador"
                  value={operatorId}
                  onChange={(e: any) => setOperatorId(e.target.value)}
                />
              </div>
              <div className="col-md-4">
                <Input
                  label="routeGroupId"
                  optional
                  type="number"
                  placeholder="10, 20, 30, 40…"
                  value={routeGroupId}
                  onChange={(e: any) => setRouteGroupId(e.target.value)}
                />
              </div>
              <div className="col-md-3">
                <button
                  type="submit"
                  className="btn btn-primary w-100 fw-bold fs-7 mb-1"
                  disabled={loading}
                >
                  <Icon icon="ki-magnifier" size="fs-4" className="me-1" />
                  {loading ? 'Resolviendo…' : 'Resolver'}
                </button>
              </div>
            </form>

            {result && (
              <>
                <div className="mb-6">
                  <div className="fs-7 fw-bold text-gray-600 mb-2">
                    Cadena de capas (la última gana)
                  </div>
                  <div className="d-flex flex-wrap align-items-center gap-2">
                    {LAYER_ORDER.map((layer, i) => (
                      <div key={layer} className="d-flex align-items-center gap-2">
                        <span
                          className={`badge fs-8 py-2 px-3 ${
                            applied.has(layer as any)
                              ? 'badge-primary'
                              : 'badge-light text-muted text-decoration-line-through'
                          }`}
                        >
                          {LAYER_LABEL[layer]}
                        </span>
                        {i < LAYER_ORDER.length - 1 && (
                          <Icon icon="ki-right" size="fs-6 text-gray-400" />
                        )}
                      </div>
                    ))}
                  </div>
                  <div className="fs-8 text-muted mt-2">
                    templateId: <code>{result.resolution.templateId ?? 'null'}</code> · operatorId:{' '}
                    <code>{result.resolution.operatorId ?? 'null'}</code> · routeGroupId:{' '}
                    <code>{result.resolution.routeGroupId ?? 'null'}</code>
                  </div>
                </div>

                <div className="row g-4">
                  <div className="col-md-6">
                    <div className="card bg-light border-0 h-100">
                      <div className="card-body p-5">
                        <div className="fs-7 fw-bold text-gray-600 mb-3">Config resuelta</div>
                        <table className="table table-sm table-row-dashed fs-7 mb-0">
                          <tbody>
                            <tr>
                              <td className="text-muted">mode</td>
                              <td className="fw-bold">{String(cfg.mode ?? '—')}</td>
                            </tr>
                            <tr>
                              <td className="text-muted">action</td>
                              <td className="fw-bold">{String(cfg.action ?? '—')}</td>
                            </tr>
                            {cfg.transferTo && (
                              <tr>
                                <td className="text-muted">transferTo</td>
                                <td>{cfg.transferTo}</td>
                              </tr>
                            )}
                            <tr>
                              <td className="text-muted">bypass</td>
                              <td>{cfg.bypass ? 'sí' : 'no'}</td>
                            </tr>
                            <tr>
                              <td className="text-muted">toneCarrier</td>
                              <td>{cfg.toneCarrier ?? '—'}</td>
                            </tr>
                            <tr>
                              <td className="text-muted">toneStartDelayMs</td>
                              <td>{cfg.toneStartDelayMs ?? '—'}</td>
                            </tr>
                            <tr>
                              <td className="text-muted">toneSpec</td>
                              <td>
                                <code className="fs-8">{cfg.toneSpec ?? '—'}</code>
                              </td>
                            </tr>
                            <tr>
                              <td className="text-muted">voiceSpec</td>
                              <td>
                                <code className="fs-8">{cfg.voiceSpec ?? '—'}</code>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="card bg-light border-0 h-100">
                      <div className="card-body p-5">
                        <div className="fs-7 fw-bold text-gray-600 mb-3">Thresholds</div>
                        <table className="table table-sm table-row-dashed fs-7 mb-0">
                          <tbody>
                            <tr>
                              <td className="text-muted">maxAnalysisMs</td>
                              <td>{th.maxAnalysisMs ?? '—'}</td>
                            </tr>
                            <tr>
                              <td className="text-muted">minEnergyDbfs</td>
                              <td>{th.minEnergyDbfs ?? '—'}</td>
                            </tr>
                            <tr>
                              <td className="text-muted">minVoiceMs</td>
                              <td>{th.minVoiceMs ?? '—'}</td>
                            </tr>
                            <tr>
                              <td className="text-muted">maxSilenceMs</td>
                              <td>{th.maxSilenceMs ?? '—'}</td>
                            </tr>
                            {th.dtmfHumanDigits !== undefined && (
                              <tr>
                                <td className="text-muted">dtmfHumanDigits</td>
                                <td>{th.dtmfHumanDigits}</td>
                              </tr>
                            )}
                            {th.mlConfidenceMin !== undefined && (
                              <tr>
                                <td className="text-muted">mlConfidenceMin</td>
                                <td>{th.mlConfidenceMin}</td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>

                <details className="mt-4">
                  <summary className="fs-8 text-muted" style={{ cursor: 'pointer' }}>
                    JSON crudo
                  </summary>
                  <pre
                    className="bg-dark text-light rounded p-4 fs-8 mt-2 mb-0"
                    style={{ maxHeight: 260, overflow: 'auto' }}
                  >
                    {JSON.stringify(result, null, 2)}
                  </pre>
                </details>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
